/**
 * Ein Klick auf das Gebaeude schlaegt Flaechen vor (PV-D17).
 *
 * Der Vorschlag ist ein normales Polygon und ab dem Moment ganz normal
 * bearbeitbar — kein Sondermodus, keine Sperre. Taugt er nichts, wird von Hand
 * gezeichnet, ohne Umweg und ohne dass man etwas abschalten muesste.
 *
 * Die Kette: Umriss holen, vereinfachen, begradigen, Luftbild abtasten, an der
 * Firstlinie teilen. Faellt einer der Schritte aus, kommt eben weniger heraus —
 * im schlechtesten Fall der blanke Gebaeudeumriss, und das ist immer noch mehr
 * als ein leeres Luftbild.
 */

import {
  begradige,
  bezugspunkt,
  nachGeo,
  ringNachGrundriss,
  teileNachHelligkeit,
  vereinfache,
  type Geo,
} from '@pv/geometrie';

import { bildausschnitt } from './daten/bildproben.ts';
import { gebaeudeAn } from './daten/gebaeude.ts';

/** Darunter wird die Teilung nicht angeboten, sondern nur der Umriss (PV-D22). */
const SICHERHEITSSCHWELLE = 0.45;

export interface Flaechenvorschlag {
  /** Ein Polygon je vorgeschlagener Dachflaeche, in Grad. */
  polygone: Geo[][];
  /** Je Polygon der Index der vorgeschlagenen Traufkante, oder null. */
  traufkanten: Array<number | null>;
  sicherheit: number;
  meldung: string;
}

export async function schlageFlaechenVor(
  projektId: string,
  klick: Geo,
  kartenvorlage: string,
  maxZoom: number,
): Promise<Flaechenvorschlag | undefined> {
  const gebaeude = await gebaeudeAn(projektId, klick);
  if (!gebaeude) return undefined;

  const bezug = bezugspunkt(gebaeude.umriss);
  const umriss = begradige(vereinfache(ringNachGrundriss(gebaeude.umriss, bezug), 0.35));
  const nurUmriss = (meldung: string): Flaechenvorschlag => ({
    polygone: [umriss.map((p) => nachGeo(p, bezug))],
    traufkanten: [null],
    sicherheit: 0,
    meldung,
  });

  const ausschnitt = await bildausschnitt(gebaeude.umriss, bezug, kartenvorlage, maxZoom);
  if (!ausschnitt) {
    return nurUmriss(
      'Gebäudeumriss übernommen. Das Luftbild liegt hier nicht vor — Teilung und Traufkante bitte selbst setzen.',
    );
  }

  const teilung = teileNachHelligkeit(umriss, ausschnitt.probe);
  if (!teilung || teilung.sicherheit < SICHERHEITSSCHWELLE) {
    return nurUmriss(
      teilung
        ? `Gebäudeumriss übernommen. Die Dachteilung ist im Bild nicht deutlich genug (${Math.round(teilung.sicherheit * 100)} %) — bitte selbst teilen.`
        : 'Gebäudeumriss übernommen. Im Luftbild ist keine Firstlinie zu erkennen — bitte selbst teilen.',
    );
  }

  return {
    polygone: teilung.teile.map((teil) => teil.map((p) => nachGeo(p, bezug))),
    traufkanten: teilung.traufkanten,
    sicherheit: teilung.sicherheit,
    meldung:
      `Zwei Dachflächen vorgeschlagen, Sicherheit ${Math.round(teilung.sicherheit * 100)} %. ` +
      'Traufkanten sind vorbelegt — bitte kurz nachsehen, bevor belegt wird.',
  };
}
